import { useEffect, useRef, useState } from 'react';

import {
  HotelSavedCardStatus,
  useHotelCheckoutPaymentMethodsQuery,
} from 'generated/graphql';
import { useAppSelector } from 'store/store';

import { useHotelSavedCardRefresh } from './useHotelSavedCardRefresh';

/** An explicit checkout choice takes precedence over the personal default. */
export function useHotelSavedCardSelection(identity: string) {
  const enabled = useAppSelector(
    (state) => state.enums.featureFlags?.flags.hotelSavedCards === true,
  );
  const cards = useHotelCheckoutPaymentMethodsQuery({
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const required = enabled
    ? cards.data?.hotelTravelerCardPolicy.required
    : false;
  const [choice, setChoice] = useState<{ identity: string; id: string } | null>(
    null,
  );
  const refetch = cards.refetch;
  const current = useRef(identity);
  current.current = identity;
  const [checkedIdentity, setCheckedIdentity] = useState(identity);
  const [identityError, setIdentityError] = useState<Error | null>(null);
  const sameIdentity = checkedIdentity === identity;
  useEffect(() => {
    if (!enabled) {
      setChoice(null);
      return;
    }
    if (sameIdentity) return;
    let active = true;
    setChoice(null);
    setIdentityError(null);
    void (async () => {
      try {
        await refetch();
        if (active && current.current === identity)
          setCheckedIdentity(identity);
      } catch {
        if (active && current.current === identity)
          setIdentityError(new Error('Unable to check saved cards.'));
      }
    })();
    return () => {
      active = false;
    };
  }, [enabled, identity, sameIdentity, refetch]);
  const methods =
    enabled && sameIdentity
      ? (cards.data?.myHotelSavedCards.methods ?? [])
      : [];
  const available = methods.filter(
    (method) => method.status === HotelSavedCardStatus.Available,
  );
  const explicit = choice?.identity === identity;
  const selectedId =
    enabled && sameIdentity && required === true
      ? explicit
        ? choice.id
        : ((available.find((method) => method.isDefault) ?? available[0])?.id ??
          null)
      : null;
  const selected = available.find((method) => method.id === selectedId);
  const ready =
    sameIdentity &&
    !cards.loading &&
    !cards.error &&
    required != null &&
    (required === false || !!selected);
  useHotelSavedCardRefresh(required === true, cards.refetch);
  return {
    required: enabled ? (sameIdentity ? required : undefined) : false,
    ready: enabled ? ready : true,
    selectedId: enabled ? selectedId : null,
    methods,
    configured:
      enabled &&
      sameIdentity &&
      (cards.data?.myHotelSavedCards.configured ?? false),
    loading: enabled && (!sameIdentity || cards.loading),
    error: enabled ? identityError || cards.error : null,
    refresh: cards.refetch,
    select: (id: string) => setChoice({ identity: current.current, id }),
  };
}
export type HotelSavedCardSelection = ReturnType<
  typeof useHotelSavedCardSelection
>;
